import { ColDef } from "ag-grid-enterprise";
import {
  coldefDateField,
  coldefSingleField,
  coldefUser
} from "entity-app/shared-components/log-render-components/col-defs";
import { CellValueRendererWithEditIcon } from "components/cell-renders";
import CustomDateCellEditor from "components/cell-editor/date-cell-editor";
import ShippingLocationCellEditor from "components/cell-editor/shipping-location-cell-editor";
import EnumDropdownCellEditor from "components/cell-editor/enum-drop-down-editor";
import {
  numberCellEditorParams,
  numberFilterParams
} from "components/cell-editor/numberCellEditor";
import { DateUtils } from "utils/dateutils";
import { EnumOptionsMap } from "hooks/enum-values";

export type CustomColumnConfig = {
  field: string;
  field_type:
    | "date"
    | "user"
    | "vendor"
    | "geolocation"
    | "number"
    | "text"
    | "enum";
  headerName?: string;
};

export const buildCustomFieldColumnDefs = (
  customColumns: CustomColumnConfig[],
  enumOptionsMap?: EnumOptionsMap,
  enumEditable?: boolean | ((params: any) => boolean)
): (ColDef | null)[] => {
  return customColumns.map((col) => {
    if (col.field_type === "date") {
      return coldefDateField({
        field: col.field,
        headerName: col.headerName || "",
        initialWidth: 160,
        editable: enumEditable ?? true,
        cellRenderer: CellValueRendererWithEditIcon,
        cellEditor: CustomDateCellEditor,
        valueGetter: undefined,
        valueFormatter: ({ value }) => (value ? DateUtils.format(value) : ""),
        cellEditorParams: {
          placeholder: `Select ${col.headerName || "Date"}`
        }
      });
    }
    if (col.field_type === "user") {
      return coldefUser({
        def: {
          field: col.field,
          headerName: col.headerName || "",
          editable: enumEditable ?? true,
          initialWidth: 180
        },
        user: {
          id: `${col.field}.id`,
          firstName: `${col.field}.first_name`,
          lastName: `${col.field}.last_name`
        }
      });
    }
    if (col.field_type === "vendor") {
      return coldefUser({
        def: {
          field: col.field,
          headerName: col.headerName || "",
          editable: enumEditable ?? true,
          initialWidth: 180
        },
        user: {
          id: `${col.field}.id`,
          firstName: `${col.field}.name`,
          lastName: "",
          dropdownType: "RC_dropdown"
        }
      });
    }
    if (col.field_type === "geolocation") {
      const colName = col.field.concat("_obj");
      const fieldName = col.field.concat("_obj.location_service.label");
      return coldefSingleField({
        colId: colName,
        field: fieldName,
        headerName: col.headerName || "",
        editable: enumEditable ?? true,
        initialWidth: 180,
        cellEditor: ShippingLocationCellEditor
      });
    }
    if (col.field_type === "number") {
      return coldefSingleField({
        field: col.field,
        headerName: col.headerName || "",
        initialWidth: 180,
        editable: enumEditable ?? true,
        cellEditor: "agNumberCellEditor",
        cellEditorParams: numberCellEditorParams,
        filter: "agNumberColumnFilter",
        filterParams: numberFilterParams
      });
    }
    if (col.field_type === "text") {
      return coldefSingleField({
        field: col.field,
        headerName: col.headerName || "",
        editable: enumEditable ?? true,
        initialWidth: 180
      });
    }
    if (col.field_type === "enum") {
      const fieldKey = col.field.replace("_custom_fields.", "");
      const options = enumOptionsMap?.[fieldKey] || [];
      const refData = options.reduce((acc: Record<string, string>, opt) => {
        acc[opt.id] = opt.value;
        return acc;
      }, {});
      return coldefSingleField({
        field: col.field,
        headerName: col.headerName || "",
        editable: enumEditable ?? true,
        initialWidth: 180,
        refData,
        cellRenderer: CellValueRendererWithEditIcon,
        cellEditor: EnumDropdownCellEditor,
        cellEditorParams: {
          enumOptions: options
        }
      });
    }
    return null;
  });
};
